"use client";

import { Heart } from "iconsax-reactjs";
import type { MouseEvent } from "react";
import { useFavoriteToggle } from "@/hooks/favorites";
import { cn } from "@/lib/utils";

export interface FavoriteButtonProps {
  productId: string;
  className?: string;
  size?: number;
}

/**
 * Favorite ("like") toggle for product cards.
 *
 * Lives in the card's content/footer area — on the card surface, never over
 * product photography — so it needs no scrim or shadow to stay legible.
 */
export const FavoriteButton = ({
  productId,
  className,
  size = 20,
}: FavoriteButtonProps) => {
  const { isFavorited, toggle, isPending } = useFavoriteToggle(productId);

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  };

  return (
    <button
      type="button"
      aria-label="favorite"
      aria-pressed={isFavorited}
      disabled={isPending}
      onClick={handleClick}
      className={cn(
        "flex shrink-0 cursor-pointer items-center justify-center rounded-full p-1",
        "transition-transform duration-200 ease-out hover:scale-110 active:scale-90",
        "disabled:cursor-not-allowed disabled:opacity-60",
        className,
      )}
    >
      <Heart
        size={size}
        variant={isFavorited ? "Bold" : "Linear"}
        className={cn(
          "transition-colors duration-200",
          isFavorited
            ? "text-red-500"
            : "text-text-secondary hover:text-red-400",
        )}
      />
    </button>
  );
};
